import React, { useState, useEffect, useCallback } from "react";
import { Button, Image, Confirm, Input, Icon } from "semantic-ui-react";
import { BasicModal, CanCreate, CustomTable } from "../../../components/Shared";
import { UserForm } from "../../../components/Admin/Users";
import { User } from "../../../api";
import { useAuth } from "../../../hooks";
import { buildStorageUrl } from "../../../utils";
import { image } from "../../../assets";
import "./Users.scss";

const userController = new User();

export function Users() {
  const { accessToken } = useAuth();

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [titleModal, setTitleModal] = useState("Crear nuevo usuario");
  const [selectedUser, setSelectedUser] = useState(null);

  // Confirm dialog states
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);

  // Data states
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    totalItems: 0,
    totalPages: 1,
    limit: 10,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filter states
  const [filters, setFilters] = useState({
    name: "",
    dni: "",
    phone: "",
  });
  const [searchInputs, setSearchInputs] = useState({
    name: "",
    dni: "",
    phone: "",
  });

  // Fetch paginated users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await userController.getPaginatedUsers({
        page: currentPage,
        pageSize,
        name: filters.name,
        dni: filters.dni,
        phone: filters.phone,
      });
      setUsers(response.data || []);
      setPagination({
        totalItems: response.pagination?.totalItems || 0,
        totalPages: response.pagination?.totalPages || 1,
        limit: response.pagination?.limit || pageSize,
      });
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, currentPage, pageSize, filters]);

  useEffect(() => {
    if (accessToken) {
      fetchUsers();
    }
  }, [accessToken, fetchUsers]);

  // Modal handlers
  const openCreateUser = () => {
    setSelectedUser(null);
    setTitleModal("Crear nuevo usuario");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setTitleModal("Crear nuevo usuario");
  };

  const onOpenCloseConfirm = () => {
    setShowConfirm((prev) => !prev);
    setSelectedUser(null);
  };

  const onReload = () => {
    fetchUsers();
  };

  // User actions
  const openUpdateUser = (user) => {
    setSelectedUser(user);
    setTitleModal(`Actualizar ${user.email}`);
    setShowModal(true);
  };

  const openActivateDeactivateConfirm = (user) => {
    setSelectedUser(user);
    setConfirmMessage(
      user.active
        ? `¿Desactivar usuario ${user.email}?`
        : `¿Activar usuario ${user.email}?`
    );
    setConfirmAction(() => async () => {
      try {
        await userController.updateUser(user.id, {
          active: !user.active,
        });
        onReload();
        onOpenCloseConfirm();
      } catch (error) {
        console.error(error);
      }
    });
    setShowConfirm(true);
  };

  const openDeleteConfirm = (user) => {
    setSelectedUser(user);
    setConfirmMessage(`¿Eliminar usuario ${user.email}?`);
    setConfirmAction(() => async () => {
      try {
        await userController.deleteUser(user.id);
        onReload();
        onOpenCloseConfirm();
      } catch (error) {
        console.error(error);
      }
    });
    setShowConfirm(true);
  };

  // Filter handlers
  const handleSearchInputChange = (field, value) => {
    setSearchInputs((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setFilters({ ...searchInputs });
  };

  const handleClearFilters = () => {
    setSearchInputs({ name: "", dni: "", phone: "" });
    setFilters({ name: "", dni: "", phone: "" });
    setCurrentPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Items per page handler
  const handleItemsPerPageChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Table configuration
  const tableConfig = {
    headers: [
      "Avatar",
      "Nombre Completo",
      "DNI",
      "Email",
      "Teléfono",
      "Estado",
      "Acciones",
    ],
    columnWidths: ["60px", "auto", "100px", "auto", "120px", "100px", "150px"],
    fields: [
      {
        component: "custom",
        align: "center",
        render: (row) => (
          <Image
            avatar
            src={row.avatar ? buildStorageUrl(row.avatar) : image.noAvatar}
          />
        ),
      },
      {
        field: "firstname + lastname + secondLastname",
        dataType: "text",
      },
      {
        field: "dni",
        dataType: "text",
        align: "center",
      },
      {
        field: "email",
        dataType: "text",
      },
      {
        field: "phone",
        dataType: "text",
        align: "center",
      },
      {
        component: "Label",
        field: "active",
        align: "center",
        getColor: (value) => (value ? "green" : "red"),
        getText: (value) => (value ? "ACTIVO" : "INACTIVO"),
      },
      {
        align: "center",
        buttons: [
          {
            icon: "pencil",
            color: "blue",
            placeholder: "Actualizar usuario",
            onClick: (row) => openUpdateUser(row),
            permission: { module: "user", action: "update" },
          },
          {
            icon: (row) => row.active ? "ban" : "check",
            color: (row) => row.active ? "orange" : "green",
            placeholder: "Activar/Desactivar usuario",
            onClick: (row) => openActivateDeactivateConfirm(row),
            permission: { module: "user", action: "update" },
          },
          {
            icon: "trash",
            color: "red",
            placeholder: "Eliminar usuario",
            onClick: (row) => openDeleteConfirm(row),
            permission: { module: "user", action: "delete" },
          },
        ],
      },
    ],
  };

  return (
    <>
      <div className="users-page">
        <div className="users-page__header">
          <CanCreate module="user">
            <Button
              className="users-page__add"
              primary
              onClick={openCreateUser}
            >
              Nuevo usuario
            </Button>
          </CanCreate>
        </div>

        {/* Filters */}
        <div className="users-page__filters">
          <Input
            icon="search"
            iconPosition="left"
            placeholder="Buscar por nombre..."
            value={searchInputs.name}
            onChange={(e) => handleSearchInputChange("name", e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <Input
            icon="id card"
            iconPosition="left"
            placeholder="Buscar por DNI..."
            value={searchInputs.dni}
            onChange={(e) => handleSearchInputChange("dni", e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <Input
            icon="phone"
            iconPosition="left"
            placeholder="Buscar por teléfono..."
            value={searchInputs.phone}
            onChange={(e) => handleSearchInputChange("phone", e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <Button primary onClick={handleSearch}>
            <Icon name="search" /> Buscar
          </Button>
          <Button color="grey" onClick={handleClearFilters}>
            <Icon name="eraser" /> Limpiar filtros
          </Button>
        </div>

        {/* Table */}
        <CustomTable
          tableConfig={tableConfig}
          data={users}
          pagination={pagination}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          onItemsPerPageChange={handleItemsPerPageChange}
          loading={loading}
          size="small"
          hover
          bordered
        />
      </div>

      <BasicModal
        size="small"
        show={showModal}
        close={closeModal}
        title={titleModal}
      >
        <UserForm
          close={closeModal}
          onReload={onReload}
          user={selectedUser}
        />
      </BasicModal>

      <Confirm
        confirmButton="Aceptar"
        cancelButton="Cancelar"
        open={showConfirm}
        onCancel={onOpenCloseConfirm}
        onConfirm={confirmAction}
        content={confirmMessage}
        size="mini"
      />
    </>
  );
}
